import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
    interpolate,
    runOnJS,
    useAnimatedReaction,
    useAnimatedStyle,
    useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';

import { useLeagueUpcomingEventsQuery } from '@/api/events/query.generated';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import BetrAnalytics from '@/feature/analytics/analytics';
import { CircularPager } from '@/feature/circular-pager/CircularPager';
import { getRealIndex, moddedIndex, moveToClosestIndex } from '@/feature/circular-pager/circular-helpers';
import { CircularPagerImperativeApi, CircularPagerPageProps } from '@/feature/circular-pager/types';
import { RootStackParamList } from '@/navigation/types';
import { common, designSystem } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { isPGA, isUFC } from '@/utils/league';
import { sortEvents } from '@/utils/sortEvents';

import { Handle } from '../alerts/components/Modal';
import { useLiveEventsPropsUpdate } from '../betslip-pickem/hooks/use-live-props-update';
import { useUpdatePlayerStoreWithNewData } from '../betslip-pickem/hooks/use-player-props-store';
import { SHEET_BORDER_RADIUS } from '../circular-pager/CircularPageSlot';
import {
    FantasyFullScreenPagerTransitionProvider,
    useFantasyFullScreenPagerTransition,
} from '../circular-pager/FantasyFullScreenPagerTransitionProvider';
import { ScrollableModalContextProvider } from '../circular-pager/ScrollableModalContextProvider';
import { SheetBackground } from '../circular-pager/SheetBackground';
import { useGQLRequestPolicy } from '../guest-user/hooks/use-gql-request-policy';
import { AnimatedPickBar } from '../lobby/components/Pickbar';
import { PlayerFiltersProvider } from '../lobby/hooks/use-player-filters';
import { MaxWidthWrapper } from '../responsive-design/WebComponents';
import { EventDetailsScreen } from './EventDetailsScreen';
import { SinglePlayerProfileProvider, useSinglePlayerProfile } from './SinglePlayerProfileProvider';
import { EmptyModal, EventDetailsSheetScreen } from './components/EventDetailsSheetScreen';
import {
    EVENT_CAROUSEL_HEIGHT,
    EventsCarouselTabBar,
    PGA_EVENT_CAROUSEL_HEIGHT,
} from './components/EventsCarouselTabBar';
import { SinglePlayerProfileModal } from './components/SinglePlayerProfileModal';
import { useFantasyEventsTabsStore } from './hooks/use-fantasy-events-tabs-store';

export type FantasyEventsSheetsParamsList = StackScreenProps<RootStackParamList, 'FantasyEventsSheets'>;

export const FantasyEventsSheetsScreen = () => {
    const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();

    const selectedTab = useFantasyEventsTabsStore(state => state.selectedTab);

    useFocusEffect(
        useCallback(() => {
            BetrAnalytics.trackScreenView('FantasyEventsSheets');
        }, [])
    );
    return (
        <FantasyFullScreenPagerTransitionProvider key={params.contextKey} initialIndex={selectedTab}>
            <SinglePlayerProfileProvider>
                <PlayerFiltersProvider key={params?.eventId}>
                    <FantasyEventsSheetsTabs />
                </PlayerFiltersProvider>
            </SinglePlayerProfileProvider>
        </FantasyFullScreenPagerTransitionProvider>
    );
};

const PAGE_GAP = 20;

const FantasyEventsSheetsTabs = () => {
    const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();

    const { height, width } = useWindowDimensions();

    const requestPolicy = useGQLRequestPolicy();

    const [{ data: upcomingEvents, fetching, stale }] = useLeagueUpcomingEventsQuery({
        variables: { league: params.league },
        requestPolicy,
    });

    useLiveEventsPropsUpdate({
        events: upcomingEvents?.getUpcomingEventsV2,
        options: {
            subscribeToGameClockUpdates: true,
            subscribeToMarketUpdates: true,
            debugTag: 'FantasyEventsSheetsTabs',
        },
    });
    useUpdatePlayerStoreWithNewData(upcomingEvents?.getUpcomingEventsV2);

    const sortedEvents = useMemo(() => {
        const feed = upcomingEvents?.getUpcomingEventsV2 ?? [];
        return feed.sort((a, b) => sortEvents(a, b));
    }, [upcomingEvents?.getUpcomingEventsV2]);

    const sortedEventIds = useMemo(() => {
        return sortedEvents.map(ev => ev.id);
    }, [sortedEvents]);

    const totalPages = sortedEventIds?.length || 0;

    const noData = !fetching && !stale && sortedEvents.length === 0;

    const setSelectedTab = useFantasyEventsTabsStore(state => state.actions.setSelectedTab);
    const selectedTab = useFantasyEventsTabsStore(state => state.selectedTab);

    const internalTab = useRef(selectedTab);

    const validInitialTab = totalPages === 0 ? 0 : Math.min(Math.max(selectedTab, 0), totalPages - 1);

    const { selectedTabSharedValue, pageAnim, animatedIndex } = useFantasyFullScreenPagerTransition();

    useEffect(() => {
        if (params.eventId && sortedEventIds.length > 1 && !isUFC(params?.league)) {
            const found = sortedEventIds.findIndex(id => id === params.eventId);
            if (found !== -1) {
                setSelectedTab(found);
            }
        }
    }, [params.eventId, params?.league, setSelectedTab, sortedEventIds]);

    const pagerRef = useRef<CircularPagerImperativeApi>(null);

    // if the store's selectedTab changes from outside, jump the pager
    useEffect(() => {
        if (internalTab.current !== selectedTab && totalPages > 0) {
            internalTab.current = selectedTab;
            selectedTabSharedValue.value = selectedTab;
            pagerRef.current?.setPage(moveToClosestIndex(pageAnim.value, selectedTab, totalPages), { animated: true });
        }
    }, [selectedTab, totalPages, selectedTabSharedValue, pageAnim]);

    const handlePageChange = useCallback(
        (rawIndex: number) => {
            const realIndex = totalPages === 0 ? rawIndex : moddedIndex(rawIndex, totalPages);
            runOnJS(() => {
                selectedTabSharedValue.value = realIndex;
                internalTab.current = realIndex;
                setSelectedTab(realIndex);
            })();
        },
        [setSelectedTab, selectedTabSharedValue, totalPages]
    );
    const insets = useSafeAreaInsets();

    const onlyOnePage = totalPages === 1 || params.eventId === '' || params.league === League.Ufc;

    const headerHeight = onlyOnePage ? 0 : isPGA(params.league) ? PGA_EVENT_CAROUSEL_HEIGHT : EVENT_CAROUSEL_HEIGHT;

    const ufcEventDedicatedHeaderInfo = useMemo(() => {
        const isUfcEventPageEnabled =
            isUFC(params?.league) && !!sortedEvents.find(event => event.headerImage && event.venueDetails);

        return isUfcEventPageEnabled ? sortedEvents[0] : null;
    }, [params?.league, sortedEvents]);

    const { playerProfileAnimatedIndex, isPlayerProfileModalOpen } = useSinglePlayerProfile();

    const renderWebPage = useCallback(() => {
        const eventInfo = sortedEvents[selectedTab];
        if (!eventInfo) {
            return null;
        }

        return (
            <PlayerFiltersProvider>
                <ScrollableModalContextProvider>
                    <EventDetailsScreen
                        isActive={true}
                        eventInfo={eventInfo}
                        eventDedicatedHeaderInfo={
                            isPGA(params.league) ? sortedEvents[selectedTab] : ufcEventDedicatedHeaderInfo
                        }
                    />
                </ScrollableModalContextProvider>
            </PlayerFiltersProvider>
        );
    }, [selectedTab, params.league, sortedEvents, ufcEventDedicatedHeaderInfo]);

    const renderPage = useCallback(
        ({ offset, shouldFreeze, isActive, currentIndex }: CircularPagerPageProps) => {
            const realIndex = getRealIndex(offset, currentIndex, sortedEventIds.length);
            const eventInfo = sortedEvents[realIndex];

            if (!eventInfo) {
                // If the modal is opened before any events are loaded, display an empty modal.
                // This can occur, for example, when the user launches it from a player profile sheet from lobby
                // or selects a league that has only one event.
                return <EmptyModal />;
            }

            return (
                <EventDetailsSheetScreen
                    shouldFreeze={shouldFreeze}
                    index={realIndex}
                    eventInfo={eventInfo}
                    isActive={isActive}
                    offset={offset}
                    eventDedicatedHeaderInfo={isPGA(params.league) ? sortedEvents[offset] : ufcEventDedicatedHeaderInfo}
                />
            );
        },
        [sortedEventIds.length, params.league, sortedEvents, ufcEventDedicatedHeaderInfo]
    );

    const positionStyle = isWeb ? undefined : common.absolute;

    const topContentHeight = useMemo(() => {
        if (isPGA(params.league)) {
            return PGA_EVENT_CAROUSEL_HEIGHT;
        }
        return EVENT_CAROUSEL_HEIGHT;
    }, [params.league]);

    const isEventSheetFullyOpen = useSharedValue(false);

    // Track changes to animatedIndex outside of the style (using animatedIndex.value directly in the style would cause a flicker when opening event modal)
    useAnimatedReaction(
        () => animatedIndex.value,
        currentValue => {
            isEventSheetFullyOpen.value = currentValue === 1;
        }
    );

    // This creates a smooth effect of the event sheet shrinking when the player's profile modal is opened.
    const scaleOnlyStyle = useAnimatedStyle(() => {
        const transformOrigin = isPlayerProfileModalOpen.value ? ['50%', headerHeight, 0] : ['50%', '50%', 0];

        return {
            transformOrigin,
            transform: [
                {
                    scale: interpolate(
                        playerProfileAnimatedIndex.value,
                        [-1, 0, 1],
                        [1, (width - 16) / width, (width - 16) / width]
                    ),
                },
            ],
        };
    }, [width, headerHeight]);

    // Shrink + When scrolling Player Profile to the top of the page, the Event Detail sheet also scrolls up and fixes to the top
    const scaleAndTranslateStyle = useAnimatedStyle(() => {
        const transformOrigin = isPlayerProfileModalOpen.value ? ['50%', headerHeight, 0] : ['50%', '50%', 0];
        const translateY = interpolate(playerProfileAnimatedIndex.value, [-1, 0, 1], [0, 0, -topContentHeight]);

        return {
            transformOrigin,
            transform: [
                {
                    scale: interpolate(
                        playerProfileAnimatedIndex.value,
                        [-1, 0, 1],
                        [1, (width - 16) / width, (width - 16) / width]
                    ),
                },
                // Only include translateY if the event sheet and player profile sheet are not fully open
                ...(playerProfileAnimatedIndex.value === 1 && isEventSheetFullyOpen.value ? [] : [{ translateY }]),
            ],
        };
    }, [topContentHeight, width, headerHeight]);

    const navigation = useNavigation<StackNavigationProp<RootStackParamList, 'FantasyEventsSheets'>>();

    const onClose = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    return (
        <Box width={width} height={'100%'}>
            <SheetBackground />
            {onlyOnePage ? null : (
                <MaxWidthWrapper>
                    <EventsCarouselTabBar />
                </MaxWidthWrapper>
            )}

            <Animated.View
                style={[
                    {
                        height,
                        top: insets.top,
                        width,
                        backgroundColor: isWeb ? designSystem.colors.gray8 : undefined,
                    },
                    positionStyle,
                    onlyOnePage ? scaleOnlyStyle : scaleAndTranslateStyle,
                ]}
            >
                <CircularPager
                    ref={pagerRef}
                    pageCallbackNode={pageAnim}
                    isEmpty={noData}
                    renderEmptyView={NoEvents}
                    initialIndex={validInitialTab}
                    onPageChange={handlePageChange}
                    pageGap={PAGE_GAP}
                    renderPage={isWeb ? renderWebPage : renderPage}
                    aboveSheetHeaderHeight={headerHeight}
                    onClose={onClose}
                    onlyOnePage={onlyOnePage}
                />
                <Box
                    style={{
                        transform: [{ translateY: height }],
                        width,
                        height,
                    }}
                    zIndex={-1}
                    backgroundColor={'gray8'}
                />
            </Animated.View>
            <AnimatedPickBar />
            {!isWeb ? <SinglePlayerProfileModal onlyOnePage={onlyOnePage} circularPagerRef={pagerRef} /> : null}
        </Box>
    );
};

const NoEvents = () => {
    return (
        <Box paddingHorizontal={'s24'} flex={1} backgroundColor={'gray8'} style={styles.noEventsContainer}>
            <Handle handleColor={designSystem.colors.gray5} handleContainerStyle={styles.handle} />
            <Text variant={'bodyMedium'} fontWeight={'bold'} marginTop={'s24'}>
                No events available.
            </Text>
        </Box>
    );
};

const styles = StyleSheet.create({
    noEventsContainer: {
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        borderTopRightRadius: SHEET_BORDER_RADIUS,
    },
    handle: {
        backgroundColor: 'transparent',
    },
});
